import React, {
	useMemo,
	useState,
} from 'react';
import {
	useMutation,
	useQuery,
} from 'react-query';
import {
	useSortable,
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	Checkbox,
	Col,
	Row,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/pro-light-svg-icons';
import type { PortActivityProps } from '@api/models/port-activity';
import type { CreatePortActivityRequest } from '@api/features/port-activities/createPortActivity';
import {
	createPortActivity,
	deletePortActivity,
	getPortActivities,
	reorderPortActivities,
	updatePortActivity,
} from '@client/lib/api';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import EditableTable from '@client/components/EditableTable/index';
import { EditableCell } from '@client/components/EditableTable/EditableCell';
import showErrorNotification from '@client/utils/showErrorNotification';

const TableRow = (tableRowProps: any) => {
	const key = tableRowProps['data-row-key'];

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: key,
	});

	const style: React.CSSProperties = {
		transform: CSS.Translate.toString(transform),
		transition,
		cursor: 'move',
		...(isDragging ? { position: 'relative', zIndex: 9999, backgroundColor: 'white' } : {}),
	};

	return (
		<tr
			key={key}
			{...tableRowProps}
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
		/>
	);
};

const PortActivities = () => {
	const [localPortActivities, setLocalActivities] = useState<Array<PortActivityProps>>([]);

	// Used to prevent momentary flashes during dnd
	const [renderKey, setRenderKey] = useState(0);

	const {
		refetch,
		isLoading,
	} = useQuery(
		'portActivities',
		getPortActivities,
		{
			onSuccess: (data) => setLocalActivities(data),
		},
	);

	const createMutation = useMutation(
		createPortActivity,
		{
			onSuccess: () => {
				refetch();
				showSuccessNotification('Activity created!');
			},
			onError: (e) => {
				showErrorNotification((e as unknown as Error).toString());
			},
		},
	);

	const onAdd = (values: CreatePortActivityRequest) => {
		createMutation.mutate(values);
	};

	const updateMutation = useMutation(
		updatePortActivity,
		{
			onSuccess: () => {
				refetch();
				showSuccessNotification('Activity updated!');
			},
			onError: (e) => {
				showErrorNotification((e as unknown as Error).toString());
			},
		},
	);

	const onSave = async (id: number, attributes: Partial<PortActivityProps>) => {
		if (attributes.laytime) {
			attributes.active = true;
		}

		updateMutation.mutate({
			id,
			attributes,
		});
	};

	const deleteMutation = useMutation(
		deletePortActivity,
		{ onSuccess: () => refetch() },
	);

	const onDelete = (id: number) => deleteMutation.mutate(id);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 1,
			},
		}),
	);

	const reorderMutation = useMutation(
		reorderPortActivities,
		{
			onError: () => {
				showErrorNotification('Something went wrong when reordering activities');
			},
		},
	);

	const onDragEnd = async ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) {
			return;
		}

		const activeIndex = localPortActivities.findIndex((item) => item.id === active.id);
		const overIndex = localPortActivities.findIndex((item) => item.id === over.id);

		if (activeIndex === -1 || overIndex === -1) {
			return;
		}

		const reorderedActivities = [...localPortActivities];
		const [reorderedItem] = reorderedActivities.splice(activeIndex, 1);
		reorderedActivities.splice(overIndex, 0, reorderedItem);

		setLocalActivities(reorderedActivities);
		setRenderKey((prev) => prev + 1);
		reorderMutation.mutate(reorderedActivities.map((a) => a.id));
	};

	const columns = useMemo(() => {
		return [
			{
				dataIndex: 'name',
				title: null,
				render: () => (<FontAwesomeIcon color="gray" icon={faBars} />),
			},
			{
				dataIndex: 'name',
				title: 'Type',
				editable: true,
				editingProps: {
					type: 'text',
				},
			},
			{
				type: 'checkbox',
				dataIndex: 'active',
				title: 'Active',
				editable: true,
				editingProps: {
					type: 'check',
				},
				render: (v: boolean) => (<Checkbox checked={v} disabled />),
			},
			{
				dataIndex: 'laytime',
				title: 'Laytime',
				editable: true,
				editingProps: {
					type: 'check',
				},
				render: (v: boolean) => (<Checkbox checked={v} disabled />),
			},
		];
	}, []);

	return (
		<Row>
			<Col xxl={8} xl={10} lg={16} sm={18}>
				<DndContext
					sensors={sensors}
					modifiers={[restrictToVerticalAxis]}
					onDragEnd={onDragEnd}
				>
					<SortableContext
						items={localPortActivities.map((i) => i.id)}
						strategy={verticalListSortingStrategy}
					>
						<EditableTable
							key={renderKey}
							size="small"
							allowAddNew
							addNewText="Add activity"
							onAddNew={onAdd}
							onDelete={onDelete}
							// @ts-ignore
							columns={columns}
							iconButtons
							components={{
								body: {
									row: TableRow,
									cell: EditableCell,
								},
							}}
							keyDataIndex="id"
							onSave={onSave}
							dataSource={localPortActivities}
							pagination={false}
							loading={isLoading}
							enableDelete
						/>
					</SortableContext>
				</DndContext>
			</Col>
		</Row>
	);
};

export default PortActivities;
